/**
 * =========================================================================
 * MAIN APPLICATION ROUTER
 * =========================================================================
 * 
 * This is the root router component for the entire application.
 * It configures React Router and integrates all route modules.
 * 
 * Key Features:
 * - Centralized route management
 * - Auth context integration
 * - Loading states during auth initialization
 * - Fallback routes and error handling
 * - Layout wrapper for consistent UI across all pages
 * 
 * Route Organization:
 * - Authentication routes (signin, signup, password recovery)
 * - User management routes (profile, settings, onboarding)
 * - Organization routes (dashboard, teams, members)
 * - Player routes (team requests, profile)
 * - Talent scout routes (discovery, analysis)
 * - Team management routes
 * - Member management routes
 * 
 * Auth Context Integration:
 * - Loading state management during app initialization
 * - Automatic token validation and user data fetching
 * - Seamless integration with all route modules
 * =========================================================================
 */

import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PageProvider } from "./contexts/PageContext";
import LoadingProvider from "./contexts/loadingContext";
import Layout from "./layouts/layout";
import PrivateRoute from "./core/PrivateRoute";
import PublicRoute from "./core/PublicRoute";
import { publicRoutes } from "./routes/publicRoutes";
import { privateRoutes } from "./routes/privateRoutes";
import NotFound from "./pages/404";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error?.response?.status === 401) return false;
        return failureCount < 3;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const AppRouter = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <PageProvider>
          <LoadingProvider>
            <Layout>
              <Routes>
                {/* Public Routes */}
                {publicRoutes
                  .filter(route => route.path !== "*") // Exclude wildcard from public routes
                  .map((route, index) => (
                    <Route
                      key={`public-${index}`}
                      path={route.path}
                      element={
                        <PublicRoute>
                          {route.element}
                        </PublicRoute>
                      }
                    />
                  ))}
                
                {/* Private Routes */}
                {privateRoutes.map((route, index) => (
                  <Route
                    key={`private-${index}`}
                    path={route.path}
                    element={
                      <PrivateRoute>
                        {route.element}
                      </PrivateRoute>
                    }
                  />
                ))}

                {/* 404 Catch-all Route - Must be last */}
                <Route path="*" element={
                  <PrivateRoute>
                    <NotFound />
                  </PrivateRoute>
                } />
              </Routes>
            </Layout>
          </LoadingProvider>
        </PageProvider>
      </Router>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default AppRouter;
